import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CrmSubNav } from '@/components/crm/CrmSubNav';
import { CrmEmpresaSelector } from '@/components/crm/CrmEmpresaSelector';
import { useCrmEmpresaAtiva } from '@/hooks/useCrmEmpresaAtiva';
import { crmContasService } from '@/services/crmApi';
import { CrmImportCsvDialog } from '@/components/crm/CrmImportCsvDialog';
import { CrmConta } from '@/types/crm';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Building2, Edit, Trash2, Loader2, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';

const EMPTY_FORM = {
  razao_social: '',
  cnpj_cpf: '',
  email: '',
  telefone: '',
  endereco: '',
  segmento: '',
};

export default function CrmContas() {
  const { isSuperAdmin, empresas, selectedEmpresaId, setSelectedEmpresaId, empresaId } = useCrmEmpresaAtiva();
  const [contas, setContas] = useState<CrmConta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroTexto, setFiltroTexto] = useState('');

  const contasFiltradas = useMemo(() => {
    const texto = filtroTexto.trim().toLowerCase();
    if (!texto) return contas;
    return contas.filter((c) =>
      [c.razao_social, c.cnpj_cpf, c.email].some((campo) => campo?.toLowerCase().includes(texto)),
    );
  }, [contas, filtroTexto]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState<CrmConta | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formInitial, setFormInitial] = useState(EMPTY_FORM);
  const [showUnsaved, setShowUnsaved] = useState(false);

  const hasChanges = useCallback(
    () => JSON.stringify(form) !== JSON.stringify(formInitial),
    [form, formInitial],
  );

  const loadData = async () => {
    if (isSuperAdmin && !empresaId) {
      setContas([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await crmContasService.getAll(isSuperAdmin ? empresaId : undefined);
      if (result.success) {
        setContas(result.data);
      }
    } catch (error) {
      console.error('Error loading contas:', error);
      toast.error('Erro ao carregar contas');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const handleSave = async () => {
    if (isSuperAdmin && !empresaId) {
      toast.error('Selecione uma empresa antes de criar uma conta');
      return;
    }
    try {
      const payload = {
        razao_social: form.razao_social,
        cnpj_cpf: form.cnpj_cpf || null,
        email: form.email || null,
        telefone: form.telefone || null,
        endereco: form.endereco || null,
        segmento: form.segmento || null,
        empresa_id: isSuperAdmin ? empresaId : undefined,
      };

      const result = editingConta
        ? await crmContasService.update(editingConta.id, payload)
        : await crmContasService.create(payload);

      if (result.success) {
        toast.success(editingConta ? 'Conta atualizada' : 'Conta criada');
        loadData();
        closeDialog();
      } else {
        toast.error(result.error || 'Erro ao salvar conta');
      }
    } catch (error) {
      toast.error('Erro ao salvar conta');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      const result = await crmContasService.delete(deleteConfirmId);
      if (result.success) {
        toast.success('Conta excluída');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao excluir conta');
      }
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
    setDeleteConfirmId(null);
  };

  const openEdit = (conta: CrmConta) => {
    const values = {
      razao_social: conta.razao_social,
      cnpj_cpf: conta.cnpj_cpf || '',
      email: conta.email || '',
      telefone: conta.telefone || '',
      endereco: conta.endereco || '',
      segmento: conta.segmento || '',
    };
    setEditingConta(conta);
    setForm(values);
    setFormInitial(values);
    setIsDialogOpen(true);
  };

  const openNew = () => {
    setEditingConta(null);
    setForm(EMPTY_FORM);
    setFormInitial(EMPTY_FORM);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingConta(null);
    setForm(EMPTY_FORM);
    setFormInitial(EMPTY_FORM);
  };

  const handleCloseDialog = useCallback(() => {
    if (hasChanges()) {
      setShowUnsaved(true);
    } else {
      closeDialog();
    }
  }, [hasChanges]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="animate-fade-in space-y-6">
        <CrmSubNav
          active="contas"
          right={
            isSuperAdmin ? (
              <CrmEmpresaSelector empresas={empresas} value={selectedEmpresaId} onChange={setSelectedEmpresaId} />
            ) : undefined
          }
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Contas</h1>
            <p className="text-muted-foreground">Empresas clientes cadastradas no CRM</p>
          </div>
          <div className="flex gap-2">
            <CrmImportCsvDialog
              titulo="Importar Contas via CSV"
              templateNomeArquivo="modelo-contas.csv"
              templateColunas={['razao_social', 'cnpj_cpf', 'email', 'telefone', 'segmento', 'responsavel']}
              templateExemplo={['Acme Distribuidora Ltda', '12.345.678/0001-90', 'contato@acme.com', '(11) 99999-0000', 'Varejo', '']}
              onImport={(file) => crmContasService.importCsv(file, { empresaId: isSuperAdmin ? empresaId : undefined })}
              onSuccess={loadData}
              disabled={isSuperAdmin && !empresaId}
              disabledMotivo="Selecione uma empresa primeiro"
            />
            <Button onClick={openNew} disabled={isSuperAdmin && !empresaId}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Conta
            </Button>
          </div>
        </div>

        {isSuperAdmin && !empresaId ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Selecione uma empresa acima para ver e gerenciar as contas do CRM
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por razão social, CNPJ ou e-mail..."
                  value={filtroTexto}
                  onChange={(e) => setFiltroTexto(e.target.value)}
                  className="h-9 pl-9"
                />
              </div>
              <span className="text-sm text-muted-foreground">
                {contasFiltradas.length} de {contas.length} conta(s)
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conta</TableHead>
                  <TableHead>CNPJ/CPF</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Segmento</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contasFiltradas.map((conta) => (
                  <TableRow key={conta.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{conta.razao_social}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{conta.cnpj_cpf || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[conta.email, conta.telefone].filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{conta.segmento || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" asChild title="Ver contatos">
                          <Link to={`/crm/contatos?conta_id=${conta.id}`}>
                            <Users className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(conta)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(conta.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {contasFiltradas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      {contas.length === 0 ? 'Nenhuma conta cadastrada' : 'Nenhuma conta encontrada para essa busca'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingConta ? 'Editar Conta' : 'Nova Conta'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="razao_social">Razão Social *</Label>
              <Input
                id="razao_social"
                value={form.razao_social}
                onChange={(e) => setForm((f) => ({ ...f, razao_social: e.target.value }))}
                placeholder="Ex: Acme Ltda"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cnpj_cpf">CNPJ/CPF</Label>
                <Input
                  id="cnpj_cpf"
                  value={form.cnpj_cpf}
                  onChange={(e) => setForm((f) => ({ ...f, cnpj_cpf: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="segmento">Segmento</Label>
                <Input
                  id="segmento"
                  value={form.segmento}
                  onChange={(e) => setForm((f) => ({ ...f, segmento: e.target.value }))}
                  placeholder="Ex: Varejo"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={form.telefone}
                  onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                value={form.endereco}
                onChange={(e) => setForm((f) => ({ ...f, endereco: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!form.razao_social}>
              {editingConta ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsaved}
        onOpenChange={setShowUnsaved}
        onConfirm={() => {
          setShowUnsaved(false);
          closeDialog();
        }}
        onCancel={() => setShowUnsaved(false)}
      />

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta conta? Os contatos vinculados não serão excluídos, apenas
              desvinculados. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
